/**
 * triageDecisionCheck.js
 * ----------------------
 * The wording of the "Check your decision" dialog that opens before a triage
 * decision is recorded (TDI-693; Tom King, R-025, 31 October 2026). It repeats
 * back what the clinician chose, word for word from the ticket, and nothing
 * else: it never comments on the decision.
 *
 * Pure, so the wording for each case can be tested on its own.
 */

import { PATHWAY, PATHWAY_LABELS } from './triagePathways';

export const DECISION_CHECK_TITLE = 'Check your decision';
export const DECISION_CHECK_GO_BACK = 'Go back';
export const DECISION_CHECK_RECORD = 'Record decision';

/**
 * The signposting line: the pick-list labels chosen, then the "Other" title
 * if there is one, or "None".
 *
 * @param {string[]} labels - the chosen pick-list labels, in list order
 * @param {string} [otherTitle] - the clinician's "Other" title, if any
 */
export function signpostingCheckLine(labels = [], otherTitle = '') {
  const items = [...(labels || []).filter(Boolean)];
  const other = String(otherTitle || '').trim();
  if (other) items.push(other);
  return `Signposting: ${items.length ? items.join(', ') : 'None'}.`;
}

/**
 * The dialog's body lines for one decision.
 *
 * @param {object} args
 * @param {'adhd'|'asd'} args.pathway
 * @param {boolean} args.accepted
 * @param {boolean} [args.isSecondReview] - autism second review
 * @param {string[]} [args.signpostingLabels]
 * @param {string} [args.signpostingOtherTitle]
 * @param {boolean} [args.showChangeLine] - the decision can be changed until
 *   the letter is signed off (TDI-695), so say so
 * @returns {string[]}
 */
export function decisionCheckLines({
  pathway,
  accepted,
  isSecondReview = false,
  signpostingLabels = [],
  signpostingOtherTitle = '',
  showChangeLine = false,
}) {
  const signposting = signpostingCheckLine(signpostingLabels, signpostingOtherTitle);
  const outcome = accepted ? 'Accepted onto' : 'Not accepted onto';

  if (pathway === PATHWAY.ASD) {
    // A first rejection is not final: a different clinician reviews it next,
    // and no letter is written yet.
    if (!accepted && !isSecondReview) {
      return [
        "You're recording: Not accepted at first review.",
        "A different clinician will do a second review. There's no letter yet.",
        ...(showChangeLine
          ? ['You can change your answers until a second reviewer picks it up.']
          : []),
      ];
    }
    const lines = [`You're recording: ${outcome} the autism assessment pathway.`, signposting];
    lines.push(
      isSecondReview
        ? "There's no further review after yours. You'll check the letter next."
        : "You'll check the letter next.",
    );
    // The consensus line was removed for go-live (TDI-784, R-053): the
    // platform has no consensus step, so the dialog promises none.
    if (showChangeLine) lines.push('You can change your decision until you sign off the letter.');
    return lines;
  }

  return [
    `You're recording: ${outcome} the ADHD assessment pathway.`,
    signposting,
    "You'll check the letter next.",
    ...(showChangeLine ? ['You can change your decision until you sign off the letter.'] : []),
  ];
}

/**
 * The dialog's subtitle on a combined referral, naming the pathway being
 * decided so the other pathway is never in doubt. Empty on a single referral,
 * where the outcome line already names it.
 */
export function decisionCheckSubtitle(pathway, combinedReferral) {
  if (!combinedReferral) return null;
  const name = PATHWAY_LABELS[pathway];
  return name ? `${name} referral` : null;
}
